/**
 * Per-page "About this page" content for the explainer overlay.
 *
 * Each entry follows the same three-section discipline:
 *   - whatThisIs: one or two sentences, plain language
 *   - howWeProduceIt: 2–3 bullets describing the production process
 *   - whatItAnswers: 1–3 bullets describing the questions the artifact lets you ask
 *
 * Content is Yango-specific: references the 4-week May 2026 assessment, real
 * stakeholders (Yura, Maor, Leon, Dave), real systems (Woody, S1 WMS, Tanker
 * Key, Adminca), and live risks (R11 WMS vendor, R87 Woody-import, R90
 * FIFO/FEFO).
 */

export interface PageExplainer {
  /** Title shown in the panel header — usually the page's name. */
  title: string;
  /** 1–2 sentence plain-language definition. */
  whatThisIs: string;
  /** 2–3 bullets describing the production process. */
  howWeProduceIt: readonly string[];
  /** 1–3 bullets describing the questions the artifact answers. */
  whatItAnswers: readonly string[];
}

export type PageExplainerKey =
  | "dashboard"
  | "schedule"
  | "meetings"
  | "evaluation-criteria"
  | "products"
  | "architecture"
  | "candidates"
  | "compare-summary"
  | "roadmaps"
  | "open-questions";

export const PAGE_EXPLAINERS: Record<PageExplainerKey, PageExplainer> = {
  dashboard: {
    title: "YangoDeli B2B assessment — overview",
    whatThisIs:
      "Live status of the 4-week May 2026 assessment Moveo is running for Yango on the YangoDeli B2B app — what gate we're on, what's red, and what's still open before Yura sees the recommendation.",
    howWeProduceIt: [
      "Fathom-recorded sessions with Yango (Maor, Leon, Yura) and Moveo (Dave) feed risks, decisions and open questions",
      "Each W1–W4 gate resurfaces what is due and what is still open against that week's scope",
      "Owners (Yango or Moveo) update items between sessions — no Slack threads, one source of truth",
    ],
    whatItAnswers: [
      "Are we on pace for the W4 recommendation to Yura?",
      "Which risks (R11 WMS vendor, R87 Woody-import, R90 FIFO/FEFO) block forward motion right now?",
      "Where is each open question parked, and with whom — Yango or Moveo?",
    ],
  },
  schedule: {
    title: "Assessment schedule (W1–W4)",
    whatThisIs:
      "The four-week May 2026 phase plan with weekly gates. W1 discovery, W2 system walkthroughs, W3 vendor evaluation, W4 architecture recommendation — each gate locks before the next opens.",
    howWeProduceIt: [
      "Phases were scoped at kickoff with Yango leadership and timed against Yura's availability for the W4 readout",
      "Each phase has a gate condition agreed up front (e.g. W2 ends when all six system walkthroughs are written up)",
      "Discoveries, risks and decisions roll up against the phase they belong to — not by calendar week",
    ],
    whatItAnswers: [
      "What is in scope this week and what is explicitly deferred?",
      "What must be true before we move from discovery to recommendation?",
      "Where does each Fathom recording, decision, and deliverable land on the timeline?",
    ],
  },
  meetings: {
    title: "Meeting log",
    whatThisIs:
      "Every conversation in the YangoDeli B2B assessment — kickoff, the six May system walkthroughs (PIM, BI, delivery ops, pricing, comms, WMS), discoveries with Maor and Leon, the May 19 MVP scope-shaping session.",
    howWeProduceIt: [
      "Each session is Fathom-recorded with consent; the share link lives on the meeting page",
      "Transcripts are processed into a 15-section product (A–O) via the meeting product template — exec summary, component graph, decisions, risks, action items",
      "Each meeting permanently anchors the features, risks and decisions it surfaced — searchable from any other page",
    ],
    whatItAnswers: [
      "What did Maor / Leon / Yura actually say in that session?",
      "Which decisions or risks (R87 Woody-import, the direct-supplier-delivery sketch) came out of which conversation?",
      "Who attended, who was named but not present, and where are the authority gaps?",
    ],
  },
  "evaluation-criteria": {
    title: "Evaluation rubric",
    whatThisIs:
      "The 16 weighted criteria we score every vendor and candidate architecture against — strategic fit to YangoDeli, coverage of the B2B MVP scope, Yandex-ecosystem reuse, Israeli context, vendor health. Editable live.",
    howWeProduceIt: [
      "Baseline rubric tuned for B2B retail / dark-store ops with a dedicated weight on Yandex-stack reuse",
      "Stakeholder interviews (Maor on MVP boundary, Leon on Woody dependencies) move which criteria carry more weight",
      "Weights stay editable through the assessment — every meeting can shift them; the candidate ranking re-derives instantly",
    ],
    whatItAnswers: [
      "What is Yango actually optimising for — speed-to-MVP or long-term replacement of S1?",
      "How much does Yandex-ecosystem reuse matter relative to vendor maturity?",
      "If Yura re-weights toward operational continuity, does the recommendation change?",
    ],
  },
  products: {
    title: "Product evaluation",
    whatThisIs:
      "Every vendor we evaluated for the YangoDeli B2B stack (WMS, ERP, ordering, comms, BI) scored against the rubric. Real evidence per criterion; dealbreakers flagged separately.",
    howWeProduceIt: [
      "Vendors sourced from Yango's existing stack (Woody, Adminca, Tanker Key, the Yandex WMS the team calls S1) plus market scan for the open slots (R11 WMS vendor)",
      "Each product researched against all 16 criteria with linked evidence; manual scores entered as `product_scores` rows",
      "Dealbreaker fails surface separately — a regional-fit miss in Israel is not just a low score, it's a stop",
    ],
    whatItAnswers: [
      "Which vendors are even in the running for the new B2B WMS?",
      "What does each vendor do well, and where does each fall short on Yango's weights?",
      "What are the hidden cliffs — Hebrew RTL support, kashrut metadata, exit cost from a Yandex-stack swap?",
    ],
  },
  architecture: {
    title: "Architecture map",
    whatThisIs:
      "The current YangoDeli component graph — what exists in B2C today (Woody, Adminca, S1 WMS, Tanker Key, the 17 dark stores) and where the B2B fork attaches. The visual the team points at in every standup.",
    howWeProduceIt: [
      "Synthesised from the six May system walkthroughs and the May 19 MVP scope-shaping session",
      "Each node carries its current owner (Yandex / Yango / open) and the reuse decision (integrate / build-fresh / defer)",
      "The B2B MVP boundary is drawn explicitly: dedicated catalog, manual order processing, news feed, push — everything outside the dotted line is W2+",
    ],
    whatItAnswers: [
      "Where does the B2B fork branch off the existing B2C stack?",
      "Which 52 Yandex API endpoints are reused as-is, which are wrapped, which are replaced?",
      "What breaks if S1 is replaced before Woody can import 'actual picks' from a new WMS?",
    ],
  },
  candidates: {
    title: "Architecture candidates",
    whatThisIs:
      "The two-fork pitch — a specific product-per-component composition for each viable B2B path. Reuse-heavy (lean on Yandex stack, ship fast) versus replace-heavy (new WMS, prove the S1 replacement thesis on B2B first).",
    howWeProduceIt: [
      "Each candidate is a composition: one product (or build-fresh) per component on the architecture map",
      "Component criticality weights bias the composite — WMS and ordering carry more than news feed and push",
      "Dealbreaker fails on any chosen vendor surface on the candidate, not just the product page",
    ],
    whatItAnswers: [
      "What are Yango's real options — not strategy slogans, but specific stacks Moveo could start building Monday?",
      "Which fork scores better under the live weights, and which loses on what?",
      "Where does each candidate compromise — and is Yura willing to take that trade?",
    ],
  },
  "compare-summary": {
    title: "Decision memo (W4 deliverable)",
    whatThisIs:
      "The side-by-side memo Moveo brings to Yura at the W4 gate — reuse-fork vs replace-fork, what Yango gets and what it gives up either way. The artifact, not the slides.",
    howWeProduceIt: [
      "Both finalists run through the same rubric with the same weights; the differential becomes the get/give matrix",
      "Each row is grounded in a specific Fathom moment, risk, or evidence link — no hand-waving",
      "Switching the weight preset (speed-to-MVP vs long-term replacement) rewrites the matrix live in front of Yura",
    ],
    whatItAnswers: [
      "If Yango picks the reuse-fork instead of the replace-fork, what specifically does it win and lose?",
      "Can Yura defend this trade to Yandex stakeholders and Yango finance?",
      "Six months in, when MVP feedback lands, will the reasoning still be on the page?",
    ],
  },
  roadmaps: {
    title: "Roadmap branches",
    whatThisIs:
      "What each candidate looks like as a 6–12 month build plan — milestones, sequencing, dependencies. Branched per candidate so the trade is concrete, not abstract.",
    howWeProduceIt: [
      "Drawn from the candidate's component composition — anything 'build_fresh' becomes a milestone",
      "Sequenced against the B2B MVP boundary (catalog, manual orders, news feed, push first; replacement plays later)",
      "Each milestone links back to the open question or risk that unlocks it (R87 unlocks WMS replacement work, R11 unlocks vendor selection)",
    ],
    whatItAnswers: [
      "What is Moveo actually proposing to build first under each fork?",
      "When does Yango realistically get the B2B MVP into the hands of hotels, restaurants, and malls?",
      "Which milestones depend on Leon's investigation closing, and which can start regardless?",
    ],
  },
  "open-questions": {
    title: "Open questions, risks & decisions",
    whatThisIs:
      "The live ledger of every unresolved item across the assessment — R11 (new B2B WMS vendor), R87 (Woody 'actual picks' import), R90 (FIFO/FEFO sizing), plus every smaller question parked against an owner.",
    howWeProduceIt: [
      "Items surface from Fathom sessions and are tagged with the meeting they came from",
      "Each carries an owner (Yango or Moveo) and a gate it must close before",
      "Decisions move from 'open' to 'taken' on the page — no lost context",
    ],
    whatItAnswers: [
      "What is still blocking the W4 recommendation?",
      "Whose desk is each question on — Maor, Leon, Dave, or unowned?",
      "Which authority gaps (e.g. R11 with no named owner) need a named decider this week?",
    ],
  },
} as const;
